import { useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import {
  ListOptions,
  Loader,
  NotificationType,
  Button,
  ButtonStyle,
  ButtonColor,
  Icon,
  IconType,
} from '../../../components'
import {
  GET_COLLABORATIONS_BY_CAMPAIGN_ID,
  GetCollaborationsByCampaignIdResponse,
} from '../../../gql'
import {
  CollaborationStatus,
  GetCollaborationsSort,
  QueryGetCollaborationsByCampaignIdArgs,
  User,
} from '../../../gql/types'
import { useNotification } from '../../../utils/hooks'
import { Header } from '../components/Header'
import { ApplicantsList } from './ApplicantsList'
import { Grid } from './components/Grid'
import { PublicRoutes } from '../../../utils/PublicRoutes'

const filterOptions = [
  {
    id: 'isFavorite',
    name: 'Favorites',
    options: [
      {
        value: true,
        label: 'View Favorite Applicants',
        // icon: <Icon type={IconType.heartFilled} className='h-4 w-4 text-red-500' aria-hidden='true' />
      },
      // { value: false, label: 'Not Favorites'},
    ],
  },
]

interface IProps {
  user: User
}

type TabOption = {
  id: string
  label: string
  sortBy: GetCollaborationsSort | null
  isFavorite: boolean | null
}

const ApplicantTabs = ({
  activeTab,
  onTabChange,
}: {
  activeTab: string
  onTabChange: (tab: TabOption) => void
}) => {
  const tabs: TabOption[] = [
    {
      id: 'best',
      label: 'Best applicants',
      sortBy: GetCollaborationsSort.applicantsQualityScore,
      isFavorite: null,
    },
    {
      id: 'newest',
      label: 'Newest',
      sortBy: GetCollaborationsSort.newest,
      isFavorite: null,
    },
    { id: 'shortlisted', label: 'Shortlisted', sortBy: null, isFavorite: true },
  ]

  return (
    <div className='border-b border-gray-200'>
      <nav className='-mb-px flex space-x-8 px-4' aria-label='Tabs'>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab)}
            className={`
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
              ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
            aria-current={activeTab === tab.id ? 'page' : undefined}
          >
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  )
}

const Applicants = (_props: IProps) => {
  const { addNotification } = useNotification()

  const { campaignId } = useParams()
  const [searchParams] = useSearchParams()
  const orderedVideoQuantity = searchParams.get('orderedVideoQuantity')

  const limit = 40
  const [skip, setSkip] = useState(0)

  const [query, setQuery] = useState('')
  const [filtersValue, setFiltersValue] = useState({
    status: {
      [CollaborationStatus.creatorApplied]: true,
      [CollaborationStatus.creatorAccepted]: false,
      [CollaborationStatus.productShipped]: false,
      [CollaborationStatus.productDelivered]: false,
      [CollaborationStatus.contentUploaded]: false,
      [CollaborationStatus.contentAccepted]: false,
      [CollaborationStatus.contentRejected]: false,
    },
    isFavorite: {
      ['true']: false,
      ['false']: false,
    },
  })

  const [activeTab, setActiveTab] = useState('best')
  const [activeSort, setActiveSort] = useState<GetCollaborationsSort | null>(
    GetCollaborationsSort.applicantsQualityScore,
  )
  const [favoriteFilter, setFavoriteFilter] = useState<boolean | null>(null)

  const { loading, error, data, refetch } = useQuery<
    GetCollaborationsByCampaignIdResponse,
    QueryGetCollaborationsByCampaignIdArgs
  >(GET_COLLABORATIONS_BY_CAMPAIGN_ID, {
    variables: {
      campaignId: Number(campaignId),
      options: {
        query: '',
        filters: {
          status: [CollaborationStatus.creatorApplied],
          isFavorite: favoriteFilter,
          isCreatorActive: true,
        },
        sort: activeSort || GetCollaborationsSort.applicantsQualityScore,
        skip,
        limit,
      },
    },
    notifyOnNetworkStatusChange: true, // Ensure refetch is detected
  })

  useEffect(() => {
    refetch()
  }, [skip, refetch, activeSort, favoriteFilter])

  useEffect(() => {
    if (orderedVideoQuantity) {
      addNotification(
        `You have successfully ordered ${orderedVideoQuantity} videos.`,
        NotificationType.success,
      )
    }
  }, [addNotification, orderedVideoQuantity])

  const refetchData = (query, filters, sort = activeSort) => {
    const isFavoriteFilters = Object.keys(filters.isFavorite).filter(
      (key) => filters.isFavorite[key] === true,
    )

    refetch({
      options: {
        query: query,
        filters: {
          status: Object.keys(filters.status).filter(
            (key) => filters.status[key] === true,
          ) as CollaborationStatus[],
          isFavorite:
            isFavoriteFilters.length === 1
              ? isFavoriteFilters[0] === 'true'
              : favoriteFilter,
        },
        sort: sort,
        skip: 0,
        limit,
      },
    })
  }

  const onSearch = () => {
    refetchData(query, filtersValue)
  }

  const setPage = (page: number) => {
    console.log('setPage', page, limit)
    setSkip(page * limit)
  }

  const onFiltersUpdate = (section, option) => {
    const newFiltersValue = {
      ...filtersValue,
      [section.id]: {
        ...filtersValue[section.id],
        [option.value]: !filtersValue[section.id][option.value],
      },
    }

    setFiltersValue(newFiltersValue)
    refetchData(query, newFiltersValue)
  }

  const handleTabChange = (tab: TabOption) => {
    setActiveTab(tab.id)
    setActiveSort(tab.sortBy)
    setFavoriteFilter(tab.isFavorite)

    // Reset pagination when changing tabs
    setSkip(0)
  }

  const copyPublicLink = () => {
    // Get the campaign uuid from the data
    const campaignUuid =
      data?.getCollaborationsByCampaignId?.items[0]?.campaign?.uuid

    if (!campaignUuid) {
      addNotification(
        'Campaign data is not available, cannot generate link.',
        NotificationType.error,
      )
      return
    }

    const publicURL =
      window.location.origin +
      PublicRoutes.campaignApplicantsPublic.replace(':uuid', campaignUuid)
    navigator.clipboard.writeText(publicURL)
    addNotification(
      'Public link copied to clipboard!',
      NotificationType.success,
    )
  }

  if (error) {
    console.log(error)
    return <p>There has been an error. Try refreshing the page</p>
  }

  if (!loading && !data?.getCollaborationsByCampaignId) {
    return <p>Campaign does not exist.</p>
  }

  return (
    <div>
      <Header />

      {loading && <Loader />}
      {!loading && (
        <div className='pb-4 w-auto h-fit'>
          <div className='-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8'>
            <div className='inline-block min-w-full py-2 px-1 align-middle md:px-6 lg:px-8'>
              <div className='shadow ring-1 ring-black ring-opacity-5 rounded-t'>
                <ApplicantTabs
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                />
                <ListOptions
                  query={query}
                  setQuery={setQuery}
                  onSearch={onSearch}
                  filterOptions={[]}
                  filtersValue={filtersValue}
                  onFiltersUpdate={onFiltersUpdate}
                  isSearchBarFullWidth={true}
                />
                <div className='hidden lg:block'>
                  <ApplicantsList
                    collaborations={data?.getCollaborationsByCampaignId.items}
                    pagination={data?.getCollaborationsByCampaignId?.pagination}
                    setPage={setPage}
                    onRefetch={() => refetch()}
                  />
                </div>
                <div className='block lg:hidden'>
                  <Grid
                    collaborations={data?.getCollaborationsByCampaignId.items}
                    pagination={data?.getCollaborationsByCampaignId?.pagination}
                    setPage={setPage}
                    onRefetch={() => refetch()}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export { Applicants }
